/**
 * shared/select.ts —— 下拉选择器的外观增强（2026-09-09）
 *
 * ## 为什么要有这一层
 * 原生 `<select>` 的**展开菜单由操作系统绘制**，CSS 一行都管不到：
 * macOS 上是系统蓝的高亮条 + 系统圆角，Windows 上又是另一副样子。
 * 页面其余部分全是 navy 五阶，唯独一展开下拉就冒出一块系统蓝——这是"难看"的根因，
 * 光调 `select` 自身的边框和高度解决不了。
 *
 * ## 做法：包一层，不换实现
 * **原生 `<select>` 保留在 DOM 里不动**（只做视觉隐藏 + 移出 tab 序列），
 * 旁边渲染一个 `.ps-btn` 按钮和一张 `.ps-menu` 菜单；选中时写回 `sel.value`
 * 并派发**冒泡的 change**——所以：
 *   - 页面里 `onEvent` 里认 `t.id === 'ck-team'` 的分支照旧命中（`e.target` 就是原生 select）；
 *   - `onMount` 里 `addEventListener('change')` 绑在原生 select 上的也照旧触发；
 *   - 表单读值仍是 `root.querySelector('#x').value`。
 * 一句业务代码都不用改，这是选这个做法而不是"换成自定义组件"的唯一理由。
 *
 * ⚠ 三条纪律：
 * 1. **不改 `sel` 的 id / name / value 语义**——它仍是唯一数据源，按钮只是它的显示层。
 * 2. **菜单挂在 `document.body` 上**（fixed 定位）：弹窗 `.modal-b` 有 `overflow:auto`，
 *    菜单若留在原地，超出弹窗的部分会被裁掉。z-index 85 = 高于 `.mask`(70) 与灯箱(80)、低于 toast(90)。
 * 3. **选中后先关菜单再派发 change**：change 很可能触发整页重渲染（筛选类下拉都会），
 *    菜单不在 `#main` 里、不会跟着被替换掉，顺序反了就会留下一张挂在 body 上的孤儿菜单。
 *
 * ⚠ 圆角仍是 `--r`(3px)，没有借 AI 面板那档例外（红线 1）。
 */
import { $$ } from './dom.js';

/* 只在本组件内用的两个图标：icons.ts 是一期逐字节平移过来的，不往里加东西 */
const CHEV =
  '<svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" ' +
  'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M2.4 4.4 6 8l3.6-3.6"/></svg>';
const TICK =
  '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M3.4 8.5 6.4 11.5 12.6 5"/></svg>';

interface Rec {
  sel: HTMLSelectElement;
  box: HTMLElement;
  btn: HTMLButtonElement;
  lb: HTMLElement;
}

/** 当前展开的那一个（全站同时最多一张菜单） */
let open: { rec: Rec; menu: HTMLElement; opts: HTMLElement[] } | null = null;
let active = -1; // 键盘高亮项
let typed = ''; // 首字母跳转缓冲
let typedAt = 0;

/* ===================== 增强 ===================== */

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

function label(sel: HTMLSelectElement): string {
  const o = sel.selectedOptions[0] ?? sel.options[sel.selectedIndex];
  return (o?.textContent ?? '').trim();
}

/** 把按钮上的文字同步成 select 的当前选中项 */
function sync(rec: Rec): void {
  const t = label(rec.sel);
  rec.lb.textContent = t || '请选择';
  rec.lb.classList.toggle('ph', !t);
  rec.btn.disabled = rec.sel.disabled;
}

function enhance(sel: HTMLSelectElement): void {
  /* multiple 是另一种交互（多选），本组件不接管；没有父节点的说明已被摘走 */
  if (sel.multiple || sel.dataset.psOn === '1' || !sel.parentNode) return;
  sel.dataset.psOn = '1';

  const box = document.createElement('div');
  box.className = 'ps';
  sel.parentNode.insertBefore(box, sel);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ps-btn';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  /* 无障碍名沿用原 select 的：`aria-label` 或它前面那个 <label> 的文字 */
  const al = sel.getAttribute('aria-label');
  if (al) btn.setAttribute('aria-label', al);
  btn.innerHTML = `<span class="ps-lb"></span><span class="ps-cv">${CHEV}</span>`;

  box.appendChild(btn);
  box.appendChild(sel);

  /* 视觉隐藏 + 移出 tab 序列：它只当数据源，交互全部由按钮承担 */
  sel.classList.add('ps-native');
  sel.tabIndex = -1;
  sel.setAttribute('aria-hidden', 'true');

  const rec: Rec = { sel, box, btn, lb: btn.firstElementChild as HTMLElement };
  (btn as HTMLButtonElement & { _ps?: Rec })._ps = rec;
  sync(rec);
}

/* ===================== 菜单 ===================== */

function place(menu: HTMLElement, btn: HTMLElement): void {
  const r = btn.getBoundingClientRect();
  const mw = Math.max(r.width, 148);
  menu.style.minWidth = `${mw}px`;
  menu.style.left = '0px';
  menu.style.top = '0px';
  const mh = menu.offsetHeight;
  const below = window.innerHeight - r.bottom - 10;
  const up = below < mh && r.top - 10 > below;
  menu.classList.toggle('up', up);
  const top = up ? Math.max(8, r.top - 5 - mh) : r.bottom + 5;
  let left = r.left;
  const w = menu.offsetWidth;
  if (left + w > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - w);
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

function closeMenu(back?: boolean): void {
  if (!open) return;
  const { rec, menu } = open;
  open = null;
  active = -1;
  menu.remove();
  rec.box.classList.remove('ps-open');
  rec.btn.setAttribute('aria-expanded', 'false');
  rec.btn.removeAttribute('aria-activedescendant');
  window.removeEventListener('scroll', onScroll, true);
  window.removeEventListener('resize', onScroll);
  document.removeEventListener('mousedown', onDown, true);
  if (back && document.contains(rec.btn)) rec.btn.focus();
}

/**
 * 页面滚走了就收起菜单——菜单是 fixed 定位的，按钮滚开之后它会孤零零留在原地。
 * ⚠ **必须排掉菜单自己的滚动**：`scroll` 不冒泡，但 window 上的**捕获**监听照样收得到
 *   菜单内部那一次。不排掉的话，用户想往下翻看第 10 支队伍，刚滚一格菜单就自己关了
 *   （2026-09-09 用户实测踩到）。要关的只有「页面 / 弹窗滚动，按钮已经不在原位」这一种。
 */
const onScroll = (e?: Event): void => {
  if (open && e?.target instanceof Node && open.menu.contains(e.target)) return;
  closeMenu();
};
const onDown = (e: Event): void => {
  const t = e.target;
  if (!open || !(t instanceof Node)) return;
  if (open.menu.contains(t) || open.rec.box.contains(t)) return;
  closeMenu();
};

function highlight(i: number): void {
  if (!open) return;
  const { opts, menu, rec } = open;
  if (!opts.length) return;
  const n = ((i % opts.length) + opts.length) % opts.length;
  opts.forEach((o, k) => o.classList.toggle('on', k === n));
  active = n;
  const el = opts[n];
  rec.btn.setAttribute('aria-activedescendant', el.id);
  const top = el.offsetTop;
  const bot = top + el.offsetHeight;
  if (top < menu.scrollTop) menu.scrollTop = top - 4;
  else if (bot > menu.scrollTop + menu.clientHeight) menu.scrollTop = bot - menu.clientHeight + 4;
}

function openMenu(rec: Rec): void {
  closeMenu();
  const sel = rec.sel;
  const menu = document.createElement('div');
  menu.className = 'ps-menu';
  menu.setAttribute('role', 'listbox');
  const uid = `psm${Date.now().toString(36)}`;
  menu.innerHTML = Array.from(sel.options)
    .map(
      (o, i) =>
        `<div class="ps-opt" id="${uid}-${i}" role="option" data-i="${i}"` +
        ` aria-selected="${i === sel.selectedIndex}"${o.disabled ? ' data-off="1"' : ''}>` +
        `<span class="t">${esc((o.textContent ?? '').trim())}</span>` +
        `<span class="ck">${TICK}</span></div>`,
    )
    .join('');
  document.body.appendChild(menu);

  const opts = $$<HTMLElement>('.ps-opt', menu);
  open = { rec, menu, opts };
  place(menu, rec.btn);
  rec.box.classList.add('ps-open');
  rec.btn.setAttribute('aria-expanded', 'true');
  /* 入场是一次性 transition（不占循环动画白名单）；初始态 opacity:0 必须靠这次
     rAF 摘掉，reduced-motion 下 transition 被跳过也仍然会变成可见 */
  requestAnimationFrame(() => menu.classList.add('in'));

  highlight(sel.selectedIndex < 0 ? 0 : sel.selectedIndex);

  menu.addEventListener('mousemove', (e) => {
    const t = (e.target as Element).closest<HTMLElement>('.ps-opt');
    if (t && open) highlight(open.opts.indexOf(t));
  });
  menu.addEventListener('click', (e) => {
    const t = (e.target as Element).closest<HTMLElement>('.ps-opt');
    if (t && !t.dataset.off) pick(rec, Number(t.dataset.i));
  });
  /* 菜单在 body 上（不在 .mask 里），点它同样不该被 events.ts 转发给当前页的 onEvent */
  menu.addEventListener('click', (e) => e.stopPropagation());

  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onScroll);
  document.addEventListener('mousedown', onDown, true);
}

/** 选中第 i 项：写回 select → 同步按钮 → 关菜单 → 派发 change（顺序见文件头纪律 3） */
function pick(rec: Rec, i: number): void {
  const sel = rec.sel;
  const changed = sel.selectedIndex !== i;
  sel.selectedIndex = i;
  sync(rec);
  closeMenu(true);
  if (changed) sel.dispatchEvent(new Event('change', { bubbles: true }));
}

/* ===================== 安装 ===================== */

/**
 * 就地增强 root 里所有还没接管的 select。
 * ⚠ MutationObserver 的回调是**微任务**，比同步代码晚一步；而 `openModal` 与 `render`
 *   在插入 DOM 之后【同步】就要找首个可聚焦元素 / 量宽度。所以那两处要显式调一次，
 *   否则焦点会落在马上就要被隐藏的原生 select 上（表现是"开窗后焦点不见了"）。
 *   Observer 留作兜底，管那些在别处二次渲染出来的下拉。
 */
export function enhanceSelects(root: ParentNode): void {
  $$<HTMLSelectElement>('select:not([data-ps-on])', root).forEach(enhance);
}
const scan = enhanceSelects;

/**
 * 全站安装。⚠ 只在 `main.ts` 调一次。
 * 用 MutationObserver 而不是"每次渲染后手动调"：下拉散落在页面 html()、7 个弹窗、
 * 以及弹窗内的二次渲染里，靠调用点覆盖迟早漏一个——漏了的表现是同一屏两种下拉。
 */
export function installSelects(): void {
  scan(document);
  new MutationObserver((recs) => {
    for (const r of recs) {
      for (const n of Array.from(r.addedNodes)) {
        if (!(n instanceof Element)) continue;
        if (n instanceof HTMLSelectElement) enhance(n);
        else if (n.querySelector('select')) scan(n);
      }
      /* 被移除的那张菜单如果正开着（弹窗关了、整页重渲染了），跟着收掉 */
      if (open && !document.contains(open.rec.box)) closeMenu();
    }
  }).observe(document.body, { childList: true, subtree: true });

  /* ⚠ 捕获阶段：`events.ts` 的全局 click 注册得比这里早，冒泡阶段它会先跑，
     那样 stopPropagation 拦不住——按钮的点击会被转发进当前页的 onEvent。
     按钮身上没有任何 data-*，转发进去通常什么也不会发生，但这属于"碰巧没事"，
     不该留给下一个加分支的人。 */
  document.addEventListener(
    'click',
    (e) => {
      const btn = (e.target as Element)?.closest?.<HTMLElement>('.ps-btn');
      if (!btn) return;
      const rec = (btn as HTMLElement & { _ps?: Rec })._ps;
      if (!rec || rec.btn.disabled) return;
      e.stopPropagation();
      if (open && open.rec === rec) closeMenu(true);
      else openMenu(rec);
    },
    true,
  );

  document.addEventListener('keydown', (e) => {
    const btn = (document.activeElement as Element)?.closest?.<HTMLElement>('.ps-btn');
    const rec = btn ? (btn as HTMLElement & { _ps?: Rec })._ps : null;
    if (!rec) return;
    const k = e.key;

    if (!open || open.rec !== rec) {
      if (k === 'Enter' || k === ' ' || k === 'ArrowDown' || k === 'ArrowUp') {
        e.preventDefault();
        openMenu(rec);
      }
      return;
    }

    if (k === 'Escape') {
      /* ⚠ 必须挡住冒泡：events.ts 的 Escape 分支会去关最上面那个弹窗，
         而这里用户的意图只是收起下拉 */
      e.preventDefault();
      e.stopPropagation();
      closeMenu(true);
    } else if (k === 'ArrowDown') {
      e.preventDefault();
      highlight(active + 1);
    } else if (k === 'ArrowUp') {
      e.preventDefault();
      highlight(active - 1);
    } else if (k === 'Home') {
      e.preventDefault();
      highlight(0);
    } else if (k === 'End') {
      e.preventDefault();
      highlight(open.opts.length - 1);
    } else if (k === 'Enter' || k === ' ') {
      e.preventDefault();
      e.stopPropagation();
      const t = open.opts[active];
      if (t && !t.dataset.off) pick(rec, Number(t.dataset.i));
    } else if (k === 'Tab') {
      closeMenu();
    } else if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      /* 首字母跳转：1 秒内连打累积成前缀，和原生 select 的行为一致 */
      const now = Date.now();
      typed = now - typedAt > 1000 ? k : typed + k;
      typedAt = now;
      const p = typed.toLowerCase();
      const hit = open.opts.findIndex((o) => (o.textContent ?? '').trim().toLowerCase().startsWith(p));
      if (hit >= 0) highlight(hit);
    }
  }, true);
}
